import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  CheckCircle,
  AlertCircle,
  Zap,
  Check,
  XCircle,
  Loader2,
  LogOut,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export default function TrialExpiredPage() {
  const { user, organization, userProfile, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [processingSubscribe, setProcessingSubscribe] = useState(false);
  const [processingLogout, setProcessingLogout] = useState(false);

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const handleSubscribe = async () => {
    if (!organization?.id) {
      toast({
        variant: "destructive",
        title: "Organization not found",
        description: "Please try logging in again.",
      });
      return;
    }

    setProcessingSubscribe(true);
    try {
      // Check if organization already has a stripe customer and subscription
      if (
        organization.stripe_subscription_id &&
        organization.stripe_customer_id
      ) {
        // User has an existing subscription (likely past_due or incomplete)
        // Redirect to billing portal to update payment method
        const { data, error } = await supabase.functions.invoke(
          "create-billing-portal",
          {
            body: {
              customerId: organization.stripe_customer_id,
              returnUrl: window.location.origin + "/dashboard",
            },
          },
        );

        if (error) throw error;
        if (data?.url) {
          window.location.href = data.url;
        } else {
          throw new Error("No billing portal URL returned");
        }
      } else {
        // No existing subscription, create new checkout
        const starterPlanId = "cbc50e07-e21b-42af-a3dd-9c1c8d9a6334";

        const { data, error } = await supabase.functions.invoke(
          "create-stripe-checkout",
          {
            body: {
              organizationId: organization.id,
              planId: starterPlanId,
            },
          },
        );

        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        if (!data?.url) throw new Error("No session URL returned");

        window.location.href = data.url;
      }
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description:
          error.message || "Failed to process subscription. Please try again.",
      });
    } finally {
      setProcessingSubscribe(false);
    }
  };

  const handleContactSupport = () => {
    navigate("/support");
  };

  const handleLogout = async () => {
    setProcessingLogout(true);
    try {
      await signOut();
      navigate("/login");
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Logout Error",
        description: error.message || "Failed to logout. Please try again.",
      });
      setProcessingLogout(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-t from-amber-100/30 via-amber-50/20 to-white relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute -left-16 -top-16 w-32 h-32 bg-gradient-to-t from-yellow-400 to-amber-300 rounded-full opacity-10 blur-2xl"></div>
      <div className="absolute -right-16 -bottom-16 w-32 h-32 bg-gradient-to-t from-amber-400 to-yellow-300 rounded-full opacity-10 blur-2xl"></div>

      {/* WhatsApp-style Background Pattern Overlay */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${(import.meta as any).env.VITE_SUPABASE_URL}/storage/v1/object/public/image_library/chat_backgrounds/Whatsapp-Style-Background.svg)`,
          backgroundSize: "97%",
          backgroundPosition: "center center",
          backgroundRepeat: "repeat",
          opacity: 0.02,
          zIndex: 1,
          pointerEvents: "none",
          filter: "sepia(0.3) saturate(0.8) hue-rotate(20deg)",
        }}
      />

      {/* Main Content - Optimized for no-scroll */}
      <div className="container mx-auto px-4 max-w-5xl pt-12 pb-16 relative z-10 min-h-screen flex flex-col">
        {/* Header with Logout Button */}
        <div className="flex justify-between items-center mb-6">
          <div></div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            disabled={processingLogout}
            className="flex items-center gap-2"
          >
            {processingLogout ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Logging out...
              </>
            ) : (
              <>
                <LogOut className="h-4 w-4" />
                Logout
              </>
            )}
          </Button>
        </div>

        {/* Compact Hero Section with Alert */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-2 mb-6">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <span className="text-sm text-amber-800 font-medium">
              Trial Expired
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            Your 3-day trial has ended
          </h1>

          <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
            Hi {userProfile?.first_name || "there"}! Your AI agents are paused.
            Add a payment method to instantly reactivate them.
          </p>
        </div>

        {/* Two Column Layout - Starter Plan Card and CTA side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Left Column - Starter Plan Card */}
          <Card className="flex flex-col relative bg-white rounded-2xl shadow-sm border border-gray-100">
            <CardHeader className="p-6">
              <CardTitle className="text-2xl font-bold">Starter</CardTitle>
              <div className="mt-4 flex items-baseline">
                <span
                  className="text-4xl font-bold tracking-tight"
                  style={{ color: "#8aa3d9" }}
                >
                  $69
                </span>
                <span className="ml-1 text-xl text-gray-500">/month</span>
              </div>
              <CardDescription className="mt-4 text-sm">
                Get started with powerful AI chatbots
              </CardDescription>
            </CardHeader>

            <CardContent className="flex-grow p-6 pt-0">
              <ul className="space-y-2 mb-6">
                <li className="flex items-start">
                  <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Unlimited AI assistants</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">700 AI outputs/month</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">1 user</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">5 knowledge sources</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">100MB storage</span>
                </li>
                <li className="flex items-start">
                  <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-sm">Email support</span>
                </li>
              </ul>

              <Button
                className="w-full text-base"
                size="lg"
                onClick={handleSubscribe}
                disabled={processingSubscribe}
                style={{ backgroundColor: "#8aa3d9", borderColor: "#8aa3d9" }}
                onMouseEnter={(e) => {
                  if (!processingSubscribe) {
                    e.currentTarget.style.backgroundColor = "#7691d1";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!processingSubscribe) {
                    e.currentTarget.style.backgroundColor = "#8aa3d9";
                  }
                }}
              >
                {processingSubscribe ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  "Upgrade Now"
                )}
              </Button>

              <div className="flex items-center justify-center mt-2">
                <XCircle
                  className="h-4 w-4 mr-1"
                  style={{ color: "#8aa3d9" }}
                />
                <span className="text-sm text-gray-500">Cancel anytime</span>
              </div>
            </CardContent>
          </Card>

          {/* Right Column - Primary CTA */}
          <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl p-6 lg:p-8 flex flex-col justify-center">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-primary/20 rounded-full mb-4">
                <Zap className="h-6 w-6 text-primary" />
              </div>

              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                Ready to continue?
              </h2>

              <p className="text-sm sm:text-base text-gray-600 mb-6">
                Your agents will be back online instantly
              </p>

              <div className="flex flex-col items-center gap-4">
                <p className="text-sm text-gray-600 mb-2">What's paused:</p>
                <ul className="space-y-2 text-sm text-gray-600 mb-4">
                  <li className="flex items-center gap-2">
                    <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                    <span>AI agents won't respond to new chats</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                    <span>Voice agents are offline</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                    <span>Your data is safe & waiting</span>
                  </li>
                </ul>
              </div>

              <div className="mt-4 space-y-2">
                <p className="text-xs text-gray-500">
                  No setup fees • Cancel anytime
                </p>
                <Button
                  variant="link"
                  onClick={handleContactSupport}
                  className="text-sm text-gray-600 hover:text-gray-900"
                >
                  Need help? Contact support
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Compact Benefits Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-2xl">
          {[
            {
              icon: CheckCircle,
              title: "Instant Reactivation",
              description: "Back online immediately",
            },
            {
              icon: CheckCircle,
              title: "Everything Saved",
              description: "Pick up where you left off",
            },
            {
              icon: CheckCircle,
              title: "Cancel Anytime",
              description: "No contracts or fees",
            },
          ].map((benefit, index) => (
            <div key={index} className="flex items-start gap-3">
              <benefit.icon className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-sm text-gray-900">
                  {benefit.title}
                </h3>
                <p className="text-xs text-gray-600">{benefit.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
